import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { MsalProvider } from '@azure/msal-react';
import React, { useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux";
import { setToken } from "./redux/slices/userSlice";

import Login from "./pages/Login";
import NotFound from "./pages/Error";
import Menu from "./pages/Menu";
import ProductionMi from "./pages/ProductionMi";
import ProductionOh from "./pages/ProductionOh";
import ProductionMUSe from "./pages/ProductionMUSe";
import BuildStatusMiFront from "./pages/BuildStatusMiFront";
import BuildStatusMiRear from "./pages/BuildStatusMiRear";
import BuildStatusOh from "./pages/BuildStatusOh";
import BuildStatusMUSe from "./pages/BuildStatusMUSe";

function App({ instance }) {
  const userToken = useSelector((state) => state?.user?.token);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const inactivityTimerRef = useRef(null);
  const lastActivityAtRef = useRef(Date.now());

  useEffect(() => {
    const ACTIVITY_EVENTS = [
      "mousemove",
      "click",
      "scroll",
      "touchstart",
      "touchmove",
    ];
    const INACTIVITY_LOGOUT_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes

    if (location.pathname === "/") {
      return;
    }

    const logout = async () => {
      sessionStorage.setItem(
        "sessionTimeoutMessage",
        "Your session has expired due to inactivity."
      );

      dispatch(setToken({ token: "" }));
      localStorage.removeItem("persist:store");

      await instance.clearCache().catch(console.error);
      instance.setActiveAccount(null);

      window.location.replace("/");
    };

    const checkTimeout = () => {
      const inactiveTime = Date.now() - lastActivityAtRef.current;

      if (inactiveTime >= INACTIVITY_LOGOUT_TIMEOUT_MS) {
        logout();
        return;
      }

      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = setTimeout(
        checkTimeout,
        INACTIVITY_LOGOUT_TIMEOUT_MS - inactiveTime
      );
    };

    const resetTimer = () => {
      lastActivityAtRef.current = Date.now();
      checkTimeout();
    };

    ACTIVITY_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, resetTimer, { passive: true });
    });

    document.addEventListener("visibilitychange", checkTimeout);
    window.addEventListener("focus", checkTimeout);
    window.addEventListener("pageshow", checkTimeout);

    resetTimer();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        window.removeEventListener(eventName, resetTimer);
      });

      document.removeEventListener("visibilitychange", checkTimeout);
      window.removeEventListener("focus", checkTimeout);
      window.removeEventListener("pageshow", checkTimeout);

      clearTimeout(inactivityTimerRef.current);
    };
  }, [location.pathname, dispatch, instance]);

  useEffect(() => {
    if (userToken === "error") {
      navigate("/");
      const account = instance.getActiveAccount();
      if (account) {
        console.log("account", account);
        const logoutRequest = {
          // idTokenHint: accessToken,
          account: account,
          postLogoutRedirectUri: "/",
          mainWindowRedirectUri: "/",
        };
        instance.logoutRedirect(logoutRequest).catch((e) => {
          console.error(e);
        });
      }
    }
  }, [userToken]);

  return (
    <MsalProvider instance={instance}>
      <Routes>
        <Route>
          <Route path="/" element={<Login />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        <Route>
          <Route exact path="/mes/menu" element={<Menu />} />
          <Route exact path="/mes/production/mi" element={<ProductionMi />} />
          <Route exact path="/mes/production/oh" element={<ProductionOh />} />
          <Route exact path="/mes/production/muse" element={<ProductionMUSe />} />
          <Route exact path="/mes/buildstatus/mi/front" element={<BuildStatusMiFront />} />
          <Route exact path="/mes/buildstatus/mi/rear" element={<BuildStatusMiRear />} />
          <Route exact path="/mes/buildstatus/oh" element={<BuildStatusOh />} />
          <Route exact path="/mes/buildstatus/muse" element={<BuildStatusMUSe />} />
        </Route>
      </Routes>
    </MsalProvider>
  );
}

export default App;
